import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Grid,
  MenuItem,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomTextInput from 'components/Forms/CustomTextInput';
import LabelRequired from 'components/FormFields/LabelRequired';
import {
  ACTION_SCOPES,
  SCOPE_OPTIONS,
} from 'pages/PosSettings/Promotions/promotionAction';

const FIELD_SX = { width: '100%' };

/**
 * What an order-level action of each type does. Its items are the whole order,
 * or only the chosen products and categories, counted together.
 */
const ORDER_LEVEL_HINTS = {
  PERCENTAGE_DISCOUNT: 'The discount applies to all of these items.',
  FIXED_DISCOUNT:
    'The amount comes off once, split across these items.',
  BUY_X_GET_Y:
    'All of these items count towards the quantities together, and the cheapest of them are the ones the customer gets.',
  FREE_ITEM: 'The cheapest of these items is the one the customer gets.',
  FREE_DELIVERY:
    'The delivery fee is waived on any order, or only when one of the chosen products or categories is in the cart.',
  PRICE_BY_QUANTITY:
    'All of these items count towards the quantity together, and the most expensive of them are grouped first.',
};

// Kept as text so anything that is not a number stays on screen and can be
// reported, rather than being blanked by the browser's number input.
const WHOLE_NUMBER = { inputMode: 'numeric' };
const AMOUNT = { inputMode: 'decimal' };
const PAGE_SIZE = 20;
const defaultFilter = createFilterOptions();

export const LazyAutocomplete = ({
  options,
  optionFilter = defaultFilter,
  onInputChange,
  ...props
}) => {
  const [limit, setLimit] = useState(PAGE_SIZE);
  return (
    <Autocomplete
      {...props}
      options={options}
      filterOptions={(opts, state) => {
        const filtered = optionFilter(opts, state);
        return filtered.slice(0, limit);
      }}
      ListboxProps={{
        onScroll: (e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.target;
          if (scrollHeight - scrollTop - clientHeight < 50) {
            setLimit((prev) => Math.min(prev + PAGE_SIZE, options.length));
          }
        },
      }}
      onInputChange={(event, inputValue, reason) => {
        if (reason === 'input') setLimit(PAGE_SIZE);
        onInputChange?.(event, inputValue, reason);
      }}
      onOpen={() => setLimit(PAGE_SIZE)}
    />
  );
};

const productFilter = createFilterOptions({
  stringify: (option) =>
    [option.label, ...(option.barcodeNumbers || [])].join(' '),
});

const findProductsByBarcode = (productOptions, barcode) =>
  productOptions.filter((option) =>
    (option.barcodeNumbers || []).some(
      (itemBarcode) => String(itemBarcode).trim() === barcode
    )
  );

export const ProductPicker = ({
  value,
  onChange,
  onScan,
  productOptions,
  label,
  required,
  t,
}) => {
  const [inputValue, setInputValue] = useState('');

  // Autocomplete clears a controlled input whenever the `value` array identity
  // changes, so it must stay stable while the user types.
  const selectedKey = (value || []).join(',');
  const selectedProducts = useMemo(() => {
    const ids = selectedKey ? selectedKey.split(',') : [];
    return productOptions.filter((o) => ids.includes(o.value));
  }, [productOptions, selectedKey]);

  // A barcode scanner types the full code into the input, so a barcode that
  // belongs to exactly one product selects it without waiting for a click.
  // When several products share the barcode it stays a plain search, so the
  // user picks the intended one from the list.
  const handleInputChange = (_, newInputValue, reason) => {
    if (reason !== 'input') {
      setInputValue(newInputValue);
      return;
    }

    const barcode = newInputValue.trim();
    const scanned = barcode
      ? findProductsByBarcode(productOptions, barcode)
      : [];

    if (scanned.length !== 1) {
      setInputValue(newInputValue);
      return;
    }

    onScan(scanned[0].value);
    setInputValue('');
  };

  // Scanners send Enter after the code. Autocomplete would select whichever
  // option is highlighted, and the key would otherwise submit the dialog form.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.defaultMuiPrevented = true;
  };

  return (
    <LazyAutocomplete
      multiple
      options={productOptions}
      optionFilter={productFilter}
      groupBy={(option) => option.category || ''}
      getOptionLabel={(option) => option.label || ''}
      value={selectedProducts}
      onChange={(_, newVal) => onChange(newVal.map((v) => v.value))}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={required ? <LabelRequired>{label}</LabelRequired> : label}
          placeholder={t('Search by name or barcode, or scan a barcode')}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      )}
      renderTags={(val, getTagProps) =>
        val.map((option, i) => (
          <Chip
            label={option.label}
            size="small"
            {...getTagProps({ index: i })}
            key={option.value}
          />
        ))
      }
    />
  );
};

export const CategoryPicker = ({
  value,
  onChange,
  categoryOptions,
  label,
  t,
}) => (
  <LazyAutocomplete
    multiple
    options={categoryOptions}
    getOptionLabel={(option) => option.label || ''}
    value={categoryOptions.filter((o) => (value || []).includes(o.value))}
    onChange={(_, newVal) => onChange(newVal.map((v) => v.value))}
    isOptionEqualToValue={(option, val) => option.value === val.value}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        label={label}
        placeholder={t('Select categories')}
        size="small"
        InputLabelProps={{ shrink: true }}
      />
    )}
    renderTags={(val, getTagProps) =>
      val.map((option, i) => (
        <Chip
          label={option.label}
          size="small"
          {...getTagProps({ index: i })}
          key={option.value}
        />
      ))
    }
  />
);

const LockedField = ({ label, value }) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    label={label}
    value={value || ''}
    disabled
    InputLabelProps={{ shrink: true }}
  />
);

/**
 * Product and category pickers, shown for every promotion type. When the form
 * is opened from a menu item or category, that side is locked and the other is
 * hidden so the promotion stays bound to where it was created from.
 */
const TargetFields = ({
  productOptions,
  categoryOptions,
  lockedProductIds,
  lockedCategoryIds,
  lockedProductName,
  lockedCategoryName,
}) => {
  const { t } = useTranslation();
  const { watch, setValue, getValues, formState } = useFormContext();

  const targetProductIds = watch('actionTargetItemUuids') || [];
  const targetCategoryIds = watch('actionTargetCategoryUuids') || [];
  const targetsError = formState.errors?.actionTargetItemUuids?.message;

  const setTargets = (name, ids) =>
    setValue(name, ids, { shouldDirty: true, shouldValidate: true });

  // Reads the live field value so a scan appends to the current selection
  // instead of overwriting it with a stale render's list.
  const addTargetProduct = (productId) => {
    const current = getValues('actionTargetItemUuids') || [];
    if (current.includes(productId)) return;
    setTargets('actionTargetItemUuids', [...current, productId]);
  };

  return (
    <>
      {lockedProductIds?.length ? (
        <Grid item xs={12}>
          <LockedField label={t('Target Product')} value={lockedProductName} />
        </Grid>
      ) : !lockedCategoryIds?.length ? (
        <Grid item xs={12}>
          <ProductPicker
            value={targetProductIds}
            onChange={(ids) => setTargets('actionTargetItemUuids', ids)}
            onScan={addTargetProduct}
            productOptions={productOptions}
            label={t('Target Product')}
            t={t}
          />
        </Grid>
      ) : null}
      {lockedCategoryIds?.length ? (
        <Grid item xs={12}>
          <LockedField
            label={t('Target Category')}
            value={lockedCategoryName}
          />
        </Grid>
      ) : !lockedProductIds?.length ? (
        <Grid item xs={12}>
          <CategoryPicker
            value={targetCategoryIds}
            onChange={(ids) => setTargets('actionTargetCategoryUuids', ids)}
            categoryOptions={categoryOptions}
            label={t('Target Category')}
            t={t}
          />
        </Grid>
      ) : null}
      {targetsError && (
        <Grid item xs={12}>
          <Typography variant="caption" color="error">
            {t(targetsError)}
          </Typography>
        </Grid>
      )}
    </>
  );
};

/**
 * Says outright whether the action hits the whole order or chosen products.
 * A form opened from a menu item or category is item level by definition, so
 * it shows a locked value rather than hiding the question.
 */
const ScopeSelect = ({ locked }) => {
  const { t } = useTranslation();

  if (locked) {
    return (
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label={t('Applies to')}
          value={t('Item level — chosen products or categories')}
          disabled
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      {/*
        Order level only hides the targets. What was picked stays in the form
        so switching back restores it; the save drops them.
      */}
      <CustomTextInput
        sx={FIELD_SX}
        select
        name="actionScope"
        label={<LabelRequired>{t('Applies to')}</LabelRequired>}
      >
        {SCOPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.label)}
          </MenuItem>
        ))}
      </CustomTextInput>
    </Grid>
  );
};

const DiscountModeSelect = () => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const mode = watch('actionDiscountMode');

  return (
    <>
      <Grid item xs={mode === 'discount' ? 6 : 12}>
        <CustomTextInput
          sx={FIELD_SX}
          select
          name="actionDiscountMode"
          label={
            <LabelRequired>{t('What does the customer get?')}</LabelRequired>
          }
        >
          <MenuItem value="free">{t('Free')}</MenuItem>
          <MenuItem value="discount">{t('Discount (%)')}</MenuItem>
        </CustomTextInput>
      </Grid>
      {mode === 'discount' && (
        <Grid item xs={6}>
          <CustomTextInput
            sx={FIELD_SX}
            name="actionValue"
            inputProps={WHOLE_NUMBER}
            label={<LabelRequired>{t('Discount %')}</LabelRequired>}
          />
        </Grid>
      )}
    </>
  );
};

const TypeFields = ({ type, scope }) => {
  const { t } = useTranslation();

  switch (type) {
    case 'PERCENTAGE_DISCOUNT':
      return (
        <Grid item xs={12}>
          <CustomTextInput
            sx={FIELD_SX}
            name="actionValue"
            inputProps={WHOLE_NUMBER}
            label={<LabelRequired>{t('Discount %')}</LabelRequired>}
          />
        </Grid>
      );

    case 'FIXED_DISCOUNT':
      return (
        <Grid item xs={12}>
          <CustomTextInput
            sx={FIELD_SX}
            name="actionValue"
            inputProps={AMOUNT}
            label={<LabelRequired>{t('Discount Amount')}</LabelRequired>}
          />
        </Grid>
      );

    case 'BUY_X_GET_Y':
      return (
        <>
          <Grid item xs={6}>
            <CustomTextInput
              sx={FIELD_SX}
              name="actionBuyX"
              inputProps={WHOLE_NUMBER}
              label={<LabelRequired>{t('Buy Quantity (X)')}</LabelRequired>}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextInput
              sx={FIELD_SX}
              name="actionGetY"
              inputProps={WHOLE_NUMBER}
              label={<LabelRequired>{t('Get Quantity (Y)')}</LabelRequired>}
            />
          </Grid>
          <DiscountModeSelect />
        </>
      );

    case 'FREE_ITEM':
      return (
        <>
          <DiscountModeSelect />
          {scope === ACTION_SCOPES.item && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t(
                  'Pick the products or categories the customer can choose their free or discounted item from.'
                )}
              </Typography>
            </Grid>
          )}
        </>
      );

    case 'FREE_DELIVERY':
      return (
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'Delivery fee will be waived for delivery order types (Delivery, Phone Order Delivery, Catering Delivery).'
            )}
          </Typography>
        </Grid>
      );

    case 'PRICE_BY_QUANTITY':
      return (
        <>
          <Grid item xs={6}>
            <CustomTextInput
              sx={FIELD_SX}
              name="actionBuyX"
              inputProps={WHOLE_NUMBER}
              label={<LabelRequired>{t('Quantity')}</LabelRequired>}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomTextInput
              sx={FIELD_SX}
              name="actionValue"
              inputProps={AMOUNT}
              label={<LabelRequired>{t('Bundle Price')}</LabelRequired>}
            />
          </Grid>
        </>
      );

    default:
      return null;
  }
};

/**
 * The action half of a promotion form: the order/item level choice, the fields
 * that belong to the selected type, and the pickers that name the targets -
 * required at item level, optional at order level. Must be rendered inside a
 * Grid container and a
 * FormProvider.
 */
const PromotionActionFields = ({
  type,
  productOptions = [],
  categoryOptions = [],
  lockedProductIds,
  lockedCategoryIds,
  lockedProductName,
  lockedCategoryName,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  if (!type) return null;

  const locked = !!(lockedProductIds?.length || lockedCategoryIds?.length);
  const scope = locked ? ACTION_SCOPES.item : watch('actionScope');

  return (
    <>
      <ScopeSelect locked={locked} />
      <TypeFields type={type} scope={scope} />
      {scope === ACTION_SCOPES.order && (
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'Leave the products and categories empty to use every item in the order, or pick some to use only those.'
            )}{' '}
            {t(ORDER_LEVEL_HINTS[type])}
          </Typography>
        </Grid>
      )}
      {(scope === ACTION_SCOPES.order || scope === ACTION_SCOPES.item) && (
        <TargetFields
          productOptions={productOptions}
          categoryOptions={categoryOptions}
          lockedProductIds={lockedProductIds}
          lockedCategoryIds={lockedCategoryIds}
          lockedProductName={lockedProductName}
          lockedCategoryName={lockedCategoryName}
        />
      )}
    </>
  );
};

export default PromotionActionFields;
